/**
 * CaptionsClip
 * 
 * A clip type that displays captions on the canvas, sourced from a ScriptLayerClip's
 * transcript. Supports multiple display modes including phrase display with word
 * highlighting and karaoke-style single word display.
 */

import { Clip } from '../clip';
import { VisualMixin } from '../mixins';
import { visualize } from '../mixins/visual.decorator';
import { serializable } from '../../services';
import { Timestamp } from '../../models';
import { Container, Text, TextStyle, Graphics } from 'pixi.js';
import type { ScriptLayerSegment, ScriptLayerScene } from '../script/script.interfaces';

import type { Track } from '../../tracks';
import type { ClipType } from '../clip';
import type { Anchor } from '../../types';
import type { CaptionsClipProps } from './captions.interfaces';
import {
    DEFAULT_TEXT_STYLE as defaultTextStyle,
    DEFAULT_HIGHLIGHT_STYLE as defaultHighlightStyle,
    DEFAULT_BACKGROUND as defaultBackground,
    DEFAULT_PHRASE_CONFIG as defaultPhraseConfig,
} from './captions.interfaces';
import type {
    CaptionsDisplayMode,
    CaptionsTextAlign,
    CaptionsTextBaseline,
    CaptionsTextCase,
    CaptionsTextStyle,
    CaptionsHighlightStyle,
    CaptionsBackground,
    CaptionsPhraseConfig,
    CaptionWord,
} from './captions.types';

/**
 * Internal representation of a phrase (group of words to display together)
 */
interface Phrase {
    words: CaptionWord[];
    text: string;
    startSec: number;
    endSec: number;
}

type SceneStyleOverride = {
    textStyle?: Partial<CaptionsTextStyle>;
    highlightStyle?: Partial<CaptionsHighlightStyle>;
    textAlign?: CaptionsTextAlign;
    maxWidth?: number | string;
};

/**
 * CaptionsClip - displays captions sourced from a ScriptLayerClip's transcript
 */
export class CaptionsClip extends VisualMixin(Clip<CaptionsClipProps>) {
    public readonly type: ClipType = 'captions';
    public track?: Track<CaptionsClip>;

    // ============ Configuration ============

    /** Reference to the ScriptLayerClip this caption is tied to */
    @serializable()
    public scriptClipId?: string;

    /**
     * Temporal scope of this captions clip.
     * - "all": spans the full script clip (composition-wide captions)
     * - "scene": spans only the scene it was created for
     */
    @serializable()
    private _timingScope: "all" | "scene" = "all";

    /** Display mode */
    @serializable()
    private _displayMode: CaptionsDisplayMode = 'phrase';

    /** Text alignment */
    @serializable()
    private _textAlign: CaptionsTextAlign = 'center';

    /** Text baseline */
    @serializable()
    private _textBaseline: CaptionsTextBaseline = 'middle';

    /** Text case transformation */
    @serializable()
    private _textCase: CaptionsTextCase = 'none';

    /** Maximum width for text wrapping */
    @serializable()
    private _maxWidth: number | string = '80%';

    /** Anchor point for positioning */
    private _anchor: Anchor = { x: 0.5, y: 0.5 };

    // ============ Style Configuration ============

    /** Text style */
    @serializable()
    private _textStyle: CaptionsTextStyle = { ...defaultTextStyle };

    /** Highlight style for current word */
    @serializable()
    private _highlightStyle: CaptionsHighlightStyle = { ...defaultHighlightStyle };

    /** Background configuration */
    @serializable()
    private _background: CaptionsBackground = { ...defaultBackground };

    /** Phrase configuration */
    @serializable()
    private _phraseConfig: CaptionsPhraseConfig = { ...defaultPhraseConfig };

    /**
     * Per-scene style overrides.
     *
     * Keyed by the ScriptLayerScene startSplitId (stable across insertions/deletions),
     * with a special key "__start__" for the first scene.
     */
    @serializable()
    private _sceneStyleOverrides: Record<string, SceneStyleOverride> = {};

    // ============ Word Data ============

    /** Words to display (from transcript) */
    private _words: CaptionWord[] = [];

    /** Computed phrases (cached) */
    private _phrases: Phrase[] = [];
    private _phrasesDirty: boolean = true;

    // ============ Rendering State ============

    /** Container for text elements */
    private textContainer = new Container();

    /** Background graphics */
    private backgroundGraphics = new Graphics();

    /** Highlight background for current word */
    private wordHighlightGraphics = new Graphics();

    /** Currently displayed phrase index */
    private _currentPhraseIndex: number = -1;

    /** Currently highlighted word index within phrase */
    private _currentWordIndex: number = -1;

    /** Number of words currently shown in typewriter mode */
    private _currentVisibleWordCount: number = 0;

    /** The pixi TextStyle instance */
    private pixiTextStyle: TextStyle;

    /** Text objects for phrase mode (reused to avoid recreation) */
    private textObjects: Text[] = [];

    /** Visible words after filtering through current script segments (cached). */
    private _visibleWords: CaptionWord[] = [];

    /** Track the last script segment array reference to detect edits. */
    private _lastScriptSegmentsRef: ScriptLayerSegment[] | null = null;

    /** Track the current scene key to apply per-scene style overrides. */
    private _currentSceneKey: string | null = null;

    /** Effective styles applied for the current scene (base + override). */
    private _effectiveTextStyle: CaptionsTextStyle = { ...defaultTextStyle };
    private _effectiveHighlightStyle: CaptionsHighlightStyle = { ...defaultHighlightStyle };
    private _effectiveTextAlign: CaptionsTextAlign = 'center';
    private _effectiveMaxWidth: number | string = this._maxWidth;

    // ============ Constructor ============

    constructor(props?: CaptionsClipProps) {
        super();

        // Initialize pixi text style
        this.pixiTextStyle = new TextStyle({
            fontFamily: this._effectiveTextStyle.fontFamily,
            fontSize: this._effectiveTextStyle.fontSize,
            fontWeight: this._effectiveTextStyle.fontWeight as any,
            fontStyle: this._effectiveTextStyle.fontStyle as any,
            fill: this._effectiveTextStyle.fillColor,
        });
        this.applyTextStyleToPixi();

        // Setup view hierarchy
        this.view.addChild(this.backgroundGraphics);
        this.view.addChild(this.wordHighlightGraphics);
        this.view.addChild(this.textContainer);

        // Apply props if provided
        if (props) {
            this.applyProps(props);
        }

        this.on('update', this.handleUpdate.bind(this));
    }

    // ============ Getters/Setters ============

    @serializable()
    public get displayMode(): CaptionsDisplayMode {
        return this._displayMode;
    }

    public set displayMode(value: CaptionsDisplayMode) {
        this._displayMode = value;
        this._phrasesDirty = true;
        this.trigger('update', undefined);
    }

    @serializable()
    public get timingScope(): "all" | "scene" {
        return this._timingScope;
    }

    public set timingScope(value: "all" | "scene") {
        this._timingScope = value;
    }

    @serializable()
    public get textAlign(): CaptionsTextAlign {
        return this._textAlign;
    }

    public set textAlign(value: CaptionsTextAlign) {
        this._textAlign = value;
        this.recomputeEffectiveStyle();
        this.forceRerender();
    }

    @serializable()
    public get textBaseline(): CaptionsTextBaseline {
        return this._textBaseline;
    }

    public set textBaseline(value: CaptionsTextBaseline) {
        this._textBaseline = value;
        this._anchor.y = value === 'top' ? 0 : value === 'middle' ? 0.5 : 1;
        this.updateTextAlignment();
    }

    @serializable()
    public get textCase(): CaptionsTextCase {
        return this._textCase;
    }

    public set textCase(value: CaptionsTextCase) {
        this._textCase = value;
        this._phrasesDirty = true;
        this.trigger('update', undefined);
    }

    @serializable()
    public get maxWidth(): number | string {
        return this._maxWidth;
    }

    public set maxWidth(value: number | string) {
        this._maxWidth = value;
        this._phrasesDirty = true;
        this.recomputeEffectiveStyle();
        this.forceRerender();
    }

    @serializable()
    public get anchor(): Anchor {
        return this._anchor;
    }

    public set anchor(value: Anchor | number) {
        if (typeof value === 'number') {
            this._anchor = { x: value, y: value };
        } else {
            this._anchor = value;
        }
    }

    /** Get/set the words array */
    public get words(): CaptionWord[] {
        return this._words;
    }

    public set words(value: CaptionWord[]) {
        this._words = value;
        this._phrasesDirty = true;
        this.trigger('update', undefined);
    }

    /** Get/set text style */
    public get textStyle(): CaptionsTextStyle {
        return this._textStyle;
    }

    public set textStyle(value: Partial<CaptionsTextStyle>) {
        this._textStyle = { ...this._textStyle, ...value };
        this.recomputeEffectiveStyle();
        this.forceRerender();
    }

    /** Get/set highlight style */
    public get highlightStyle(): CaptionsHighlightStyle {
        return this._highlightStyle;
    }

    public set highlightStyle(value: Partial<CaptionsHighlightStyle>) {
        this._highlightStyle = { ...this._highlightStyle, ...value };
        this.recomputeEffectiveStyle();
        this.forceRerender();
    }

    /** Get/set background */
    public get background(): CaptionsBackground {
        return this._background;
    }

    public set background(value: Partial<CaptionsBackground>) {
        this._background = { ...this._background, ...value };
        this.forceRerender();
    }

    /** Get/set phrase config */
    public get phraseConfig(): CaptionsPhraseConfig {
        return this._phraseConfig;
    }

    public set phraseConfig(value: Partial<CaptionsPhraseConfig>) {
        this._phraseConfig = { ...this._phraseConfig, ...value };
        this._phrasesDirty = true;
        this.forceRerender();
    }

    // ============ Public Methods ============

    /**
     * Set words from an array of V2TranscriptWord-compatible objects
     */
    public setWords(words: CaptionWord[]): this {
        this._words = words;
        this._phrasesDirty = true;
        return this;
    }

    /**
     * Get the human-readable name of the clip
     */
    public get name(): string {
        return this._name ?? 'Captions';
    }

    // ============ Clip Lifecycle Methods ============

    public async init(): Promise<void> {
        // Initialization logic
        this.computePhrases();
    }

    public enter(): void {
        super.enter();
        // Reset state when entering
        this._currentPhraseIndex = -1;
        this._currentWordIndex = -1;
        this._currentVisibleWordCount = 0;
    }

    public exit(): void {
        super.exit();
        // Clear display
        this.clearDisplay();
    }

    public copy(): CaptionsClip {
        const json = JSON.parse(JSON.stringify(this));
        const clip = CaptionsClip.fromJSON(json);
        clip.words = [...this._words];
        return clip;
    }

    // ============ Private Methods ============

    private applyProps(props: CaptionsClipProps): void {
        if (props.scriptClipId !== undefined) this.scriptClipId = props.scriptClipId;
        if (props.timingScope !== undefined) this._timingScope = props.timingScope;
        if (props.displayMode !== undefined) this._displayMode = props.displayMode;
        if (props.textAlign !== undefined) this._textAlign = props.textAlign;
        if (props.textBaseline !== undefined) this._textBaseline = props.textBaseline;
        if (props.textCase !== undefined) this._textCase = props.textCase;
        if (props.maxWidth !== undefined) this._maxWidth = props.maxWidth;
        if (props.textStyle) this._textStyle = { ...this._textStyle, ...props.textStyle };
        if (props.highlightStyle) this._highlightStyle = { ...this._highlightStyle, ...props.highlightStyle };
        if (props.background) this._background = { ...this._background, ...props.background };
        if (props.phraseConfig) this._phraseConfig = { ...this._phraseConfig, ...props.phraseConfig };
        if (props.words) this._words = props.words;

        // Apply visual mixin props
        if (props.position) this.position = props.position;
        if (props.scale !== undefined) this.scale = props.scale;
        if (props.rotation !== undefined) this.rotation = props.rotation;
        if (props.alpha !== undefined) this.alpha = props.alpha;

        // Apply clip props
        if (props.start !== undefined) this.start = props.start;
        if (props.stop !== undefined) this.stop = props.stop;
        if (props.name !== undefined) this._name = props.name;

        this._phrasesDirty = true;
        this.recomputeEffectiveStyle();
    }

    private applyTextStyleToPixi(): void {
        const style = this._effectiveTextStyle;
        
        this.pixiTextStyle.fontFamily = style.fontFamily;
        this.pixiTextStyle.fontSize = style.fontSize;
        this.pixiTextStyle.fontWeight = style.fontWeight as any ?? 'normal';
        this.pixiTextStyle.fontStyle = (style as any).fontStyle ?? 'normal';
        this.pixiTextStyle.fill = style.fillColor;

        // Apply stroke if defined
        if (style.stroke) {
            this.pixiTextStyle.stroke = {
                color: style.stroke.color,
                width: style.stroke.width,
                join: style.stroke.join ?? 'round',
                miterLimit: style.stroke.miterLimit,
            };
        } else {
            this.pixiTextStyle.stroke = undefined;
        }

        // Apply shadow if defined
        if (style.shadow) {
            this.pixiTextStyle.dropShadow = {
                color: style.shadow.color,
                blur: style.shadow.blur,
                distance: style.shadow.distance,
                angle: style.shadow.angle,
                alpha: style.shadow.alpha,
            };
        } else {
            this.pixiTextStyle.dropShadow = false;
        }
    }

    private handleUpdate(): void {
        this.updateTextAlignment();
    }

    private updateTextAlignment(): void {
        const align = this._effectiveTextAlign;

        // Update anchor based on alignment
        switch (align) {
            case 'left':
                this._anchor.x = 0;
                break;
            case 'center':
                this._anchor.x = 0.5;
                break;
            case 'right':
                this._anchor.x = 1;
                break;
        }

        // Update view pivot based on anchor
        const width = this.textContainer.width || 0;
        const height = this.textContainer.height || 0;
        this.view.pivot.set(width * this._anchor.x, height * this._anchor.y);
    }

    /**
     * Transform text based on textCase setting
     */
    private transformText(text: string): string {
        switch (this._textCase) {
            case 'upper':
                return text.toUpperCase();
            case 'lower':
                return text.toLowerCase();
            default:
                return text;
        }
    }

    /**
     * Compute phrases from words based on current configuration
     */
    private computePhrases(): void {
        this._phrases = [];
        this._visibleWords = [];
        this._phrasesDirty = false;

        const visibleWords = this.getVisibleWords();
        this._visibleWords = visibleWords;
        if (!visibleWords.length) return;

        const { maxWordsPerPhrase = 8, maxPhraseDuration = 3, maxPhraseLength = 50 } = this._phraseConfig;

        let currentPhrase: CaptionWord[] = [];
        let phraseText = '';
        let phraseStart = 0;

        for (const word of visibleWords) {
            // Skip words without timing
            if (word.startSec === undefined || word.endSec === undefined) continue;

            const wouldBeText = phraseText + (phraseText ? ' ' : '') + word.text;
            const wouldBeDuration = word.endSec - phraseStart;

            // Check if we should start a new phrase
            const shouldBreak =
                currentPhrase.length >= maxWordsPerPhrase ||
                wouldBeDuration > maxPhraseDuration ||
                wouldBeText.length > maxPhraseLength;

            if (shouldBreak && currentPhrase.length > 0) {
                // Save current phrase
                this._phrases.push({
                    words: currentPhrase,
                    text: this.transformText(phraseText),
                    startSec: phraseStart,
                    endSec: currentPhrase[currentPhrase.length - 1].endSec!,
                });
                currentPhrase = [];
                phraseText = '';
            }

            // Start new phrase if empty
            if (currentPhrase.length === 0) {
                phraseStart = word.startSec;
            }

            // Add word to phrase
            currentPhrase.push(word);
            phraseText = phraseText + (phraseText ? ' ' : '') + word.text;
        }

        // Don't forget the last phrase
        if (currentPhrase.length > 0) {
            this._phrases.push({
                words: currentPhrase,
                text: this.transformText(phraseText),
                startSec: phraseStart,
                endSec: currentPhrase[currentPhrase.length - 1].endSec!,
            });
        }
    }

    /**
     * Find the current phrase based on time
     */
    private findCurrentPhrase(timeSec: number): number {
        for (let i = 0; i < this._phrases.length; i++) {
            const phrase = this._phrases[i];
            if (timeSec >= phrase.startSec && timeSec <= phrase.endSec) {
                return i;
            }
        }
        return -1;
    }

    /**
     * Find the current word within a phrase based on time
     */
    private findCurrentWord(phrase: Phrase, timeSec: number): number {
        for (let i = 0; i < phrase.words.length; i++) {
            const word = phrase.words[i];
            if (word.startSec !== undefined && word.endSec !== undefined) {
                if (timeSec >= word.startSec && timeSec <= word.endSec) {
                    return i;
                }
            }
        }
        return -1;
    }

    /**
     * Update display in phrase mode (show phrase with highlighted word)
     */
    private updatePhraseMode(timeSec: number): void {
        const phraseIndex = this.findCurrentPhrase(timeSec);

        if (phraseIndex === -1) {
            // No phrase to display
            if (this._currentPhraseIndex !== -1) {
                this.clearDisplay();
                this._currentPhraseIndex = -1;
                this._currentWordIndex = -1;
            }
            return;
        }

        const phrase = this._phrases[phraseIndex];
        const wordIndex = this.findCurrentWord(phrase, timeSec);

        // Check if we need to rebuild the display
        if (phraseIndex !== this._currentPhraseIndex) {
            this._currentPhraseIndex = phraseIndex;
            this.renderPhrase(phrase, wordIndex);
        } else if (wordIndex !== this._currentWordIndex) {
            this._currentWordIndex = wordIndex;
            this.updateHighlight(phrase, wordIndex);
        }
    }

    /**
     * Update display in word mode (show single word)
     */
    private updateWordMode(timeSec: number): void {
        // Find current word directly
        let currentWord: CaptionWord | null = null;

        for (const word of this._visibleWords) {
            if (word.startSec !== undefined && word.endSec !== undefined) {
                if (timeSec >= word.startSec && timeSec <= word.endSec) {
                    currentWord = word;
                    break;
                }
            }
        }

        if (!currentWord) {
            this.clearDisplay();
            return;
        }

        // Display single word with highlight style
        this.renderSingleWord(currentWord);
    }

    /**
     * Update display in typewriter mode (build phrase word-by-word over time)
     */
    private updateTypewriterMode(timeSec: number): void {
        const phraseIndex = this.findCurrentPhrase(timeSec);

        if (phraseIndex === -1) {
            if (this._currentPhraseIndex !== -1) {
                this.clearDisplay();
                this._currentPhraseIndex = -1;
                this._currentWordIndex = -1;
                this._currentVisibleWordCount = 0;
            }
            return;
        }

        const phrase = this._phrases[phraseIndex];
        let visibleWordCount = 0;
        for (let i = 0; i < phrase.words.length; i++) {
            const word = phrase.words[i];
            if (word.startSec !== undefined && timeSec >= word.startSec) {
                visibleWordCount = i + 1;
            } else {
                break;
            }
        }

        if (visibleWordCount <= 0) {
            this.clearDisplay();
            this._currentPhraseIndex = phraseIndex;
            this._currentWordIndex = -1;
            this._currentVisibleWordCount = 0;
            return;
        }

        const activeWordIndexFromTime = this.findCurrentWord(phrase, timeSec);
        const activeWordIndex =
            activeWordIndexFromTime >= 0
                ? activeWordIndexFromTime
                : Math.max(0, visibleWordCount - 1);

        const shouldRerender =
            phraseIndex !== this._currentPhraseIndex ||
            visibleWordCount !== this._currentVisibleWordCount ||
            activeWordIndex !== this._currentWordIndex;

        if (!shouldRerender) {
            return;
        }

        this._currentPhraseIndex = phraseIndex;
        this.renderTypewriterPhrase(phrase, visibleWordCount, activeWordIndex);
    }

    /**
     * Render only the visible prefix of a phrase (for typewriter mode)
     */
    private renderTypewriterPhrase(phrase: Phrase, visibleWordCount: number, highlightIndex: number): void {
        this.textContainer.removeChildren();
        this.textObjects = [];

        const clampedVisibleCount = Math.max(0, Math.min(visibleWordCount, phrase.words.length));
        const clampedHighlightIndex = Math.max(0, Math.min(highlightIndex, clampedVisibleCount - 1));

        let xOffset = 0;
        let yOffset = 0;
        const wordSpacing = this._effectiveTextStyle.fontSize * 0.25;
        const lineHeight = this._effectiveTextStyle.fontSize * 1.2;
        const maxWidthPx = Math.max(0, this.getMaxWidthPixels());

        for (let i = 0; i < clampedVisibleCount; i++) {
            const word = phrase.words[i];
            const isHighlighted = i === clampedHighlightIndex;

            const text = new Text({
                text: this.transformText(word.text),
                style: this.getWordStyle(isHighlighted),
            });

            if (maxWidthPx > 0 && xOffset > 0 && xOffset + text.width > maxWidthPx) {
                xOffset = 0;
                yOffset += lineHeight;
            }

            text.x = xOffset;
            text.y = yOffset;

            this.textContainer.addChild(text);
            this.textObjects.push(text);

            xOffset += text.width + wordSpacing;
        }

        this._currentWordIndex = clampedHighlightIndex;
        this._currentVisibleWordCount = clampedVisibleCount;
        this.drawWordHighlight(clampedHighlightIndex);
        this.updateTextAlignment();
        this.drawBackground();
    }

    /**
     * Render a phrase with individual word Text objects
     */
    private renderPhrase(phrase: Phrase, highlightIndex: number): void {
        this.textContainer.removeChildren();
        this.textObjects = [];

        let xOffset = 0;
        let yOffset = 0;
        const wordSpacing = this._effectiveTextStyle.fontSize * 0.25; // Space between words
        const lineHeight = this._effectiveTextStyle.fontSize * 1.2;
        const maxWidthPx = Math.max(0, this.getMaxWidthPixels());

        for (let i = 0; i < phrase.words.length; i++) {
            const word = phrase.words[i];
            const isHighlighted = i === highlightIndex;
            
            const text = new Text({
                text: this.transformText(word.text),
                style: this.getWordStyle(isHighlighted),
            });

            if (maxWidthPx > 0 && xOffset > 0 && xOffset + text.width > maxWidthPx) {
                xOffset = 0;
                yOffset += lineHeight;
            }

            text.x = xOffset;
            text.y = yOffset;

            this.textContainer.addChild(text);
            this.textObjects.push(text);

            xOffset += text.width + wordSpacing;
        }

        this._currentWordIndex = highlightIndex;
        this._currentVisibleWordCount = phrase.words.length;
        this.drawWordHighlight(highlightIndex);
        this.updateTextAlignment();
        this.drawBackground();
    }

    /**
     * Update highlight on existing text objects (more efficient than re-render)
     */
    private updateHighlight(phrase: Phrase, highlightIndex: number): void {
        if (this.textObjects.length !== phrase.words.length) {
            // Mismatch, re-render
            this.renderPhrase(phrase, highlightIndex);
            return;
        }

        for (let i = 0; i < this.textObjects.length; i++) {
            const textObj = this.textObjects[i];
            const isHighlighted = i === highlightIndex;
            textObj.style = this.getWordStyle(isHighlighted);
        }

        this._currentWordIndex = highlightIndex;
        this.drawWordHighlight(highlightIndex);
    }

    /**
     * Render a single word (for word mode)
     */
    private renderSingleWord(word: CaptionWord): void {
        this.textContainer.removeChildren();

        const text = new Text({
            text: this.transformText(word.text),
            style: this.getWordStyle(true), // Always highlighted in word mode
        });

        text.x = 0;
        text.y = 0;

        this.textContainer.addChild(text);
        this.textObjects = [text];
        this._currentVisibleWordCount = 1;

        this.drawWordHighlight(0);
        this.updateTextAlignment();
        this.drawBackground();
    }

    /**
     * Get the TextStyle for a word based on highlight state
     */
    private getWordStyle(highlighted: boolean): TextStyle {
        const style = this.pixiTextStyle.clone();

        if (highlighted && this._effectiveHighlightStyle) {
            style.fill = this._effectiveHighlightStyle.color;
            
            if (this._effectiveHighlightStyle.stroke) {
                style.stroke = {
                    color: this._effectiveHighlightStyle.stroke.color,
                    width: this._effectiveHighlightStyle.stroke.width,
                    join: this._effectiveHighlightStyle.stroke.join ?? 'round',
                };
            }
        }

        return style;
    }

    private drawWordHighlight(highlightIndex: number): void {
        this.wordHighlightGraphics.clear();

        const highlightBackground = this._effectiveHighlightStyle?.background;
        if (!highlightBackground) return;
        if (highlightIndex < 0 || highlightIndex >= this.textObjects.length) return;

        const targetWord = this.textObjects[highlightIndex];
        if (!targetWord) return;

        const alpha = Number(highlightBackground.alpha ?? 1);
        if (!(alpha > 0)) return;

        const fontSize = Number(this._effectiveTextStyle.fontSize ?? 48);
        const paddingX = Number(highlightBackground.paddingX ?? Math.max(6, Math.round(fontSize * 0.14)));
        const paddingY = Number(highlightBackground.paddingY ?? Math.max(3, Math.round(fontSize * 0.08)));
        const borderRadius = Number(highlightBackground.borderRadius ?? Math.max(4, Math.round(fontSize * 0.14)));

        const x = targetWord.x - paddingX;
        const y = targetWord.y - paddingY;
        const width = targetWord.width + paddingX * 2;
        const height = targetWord.height + paddingY * 2;

        this.wordHighlightGraphics.roundRect(x, y, width, height, borderRadius);
        this.wordHighlightGraphics.fill({
            color: highlightBackground.fill,
            alpha: Math.max(0, Math.min(1, alpha)),
        });
    }

    /**
     * Draw the background box behind captions
     */
    private drawBackground(): void {
        this.backgroundGraphics.clear();

        if (this._background.alpha <= 0) return;

        const padding = this._background.padding ?? { x: 20, y: 10 };
        const borderRadius = this._background.borderRadius ?? 8;

        const width = this.textContainer.width + padding.x * 2;
        const height = this.textContainer.height + padding.y * 2;

        // Position background behind text
        this.backgroundGraphics.x = -padding.x;
        this.backgroundGraphics.y = -padding.y;

        this.backgroundGraphics.roundRect(0, 0, width, height, borderRadius);
        this.backgroundGraphics.fill({
            color: this._background.fill,
            alpha: this._background.alpha,
        });
    }

    /**
     * Clear all display elements
     */
    private clearDisplay(): void {
        this.textContainer.removeChildren();
        this.textObjects = [];
        this.backgroundGraphics.clear();
        this.wordHighlightGraphics.clear();
        this._currentPhraseIndex = -1;
        this._currentWordIndex = -1;
        this._currentVisibleWordCount = 0;
    }

    /**
     * Get computed max width in pixels
     */
    private getMaxWidthPixels(): number {
        const value = this._effectiveMaxWidth ?? this._maxWidth;
        if (typeof value === 'number') {
            return value;
        }

        // Parse percentage
        const compWidth = this.track?.composition?.width ?? 1920;
        const percent = parseFloat(String(value ?? '80%')) / 100;
        return compWidth * percent;
    }

    public set(props?: CaptionsClipProps): this {
        if (props) {
            this.applyProps(props);
        }
        this.recomputeEffectiveStyle();
        this.forceRerender();
        return this;
    }

    // ============ Scene Override Helpers ============

    public get sceneStyleOverrides(): Record<string, SceneStyleOverride> {
        return this._sceneStyleOverrides;
    }

    public updateTextStyleForScene(sceneKey: string | null, value: Partial<CaptionsTextStyle>): void {
        if (!sceneKey) {
            // Apply globally and clear per-scene overrides for the changed keys only.
            this.textStyle = value;
            try {
                const keys = Object.keys(value ?? {});
                if (keys.length > 0 && this._sceneStyleOverrides && typeof this._sceneStyleOverrides === "object") {
                    const next: Record<string, SceneStyleOverride> = { ...this._sceneStyleOverrides };
                    for (const [k, ov] of Object.entries(next)) {
                        if (!ov || typeof ov !== "object") continue;
                        if (!ov.textStyle || typeof ov.textStyle !== "object") continue;
                        const ts: any = { ...(ov.textStyle as any) };
                        for (const prop of keys) delete ts[prop];
                        const updated: any = { ...ov };
                        if (Object.keys(ts).length === 0) delete updated.textStyle;
                        else updated.textStyle = ts;
                        if (!updated.textStyle && !updated.highlightStyle && !updated.textAlign) delete next[k];
                        else next[k] = updated;
                    }
                    this._sceneStyleOverrides = next;
                }
            } catch {}
            this.recomputeEffectiveStyle();
            this.forceRerender();
            return;
        }
        const prev = this._sceneStyleOverrides[sceneKey] ?? {};
        this._sceneStyleOverrides = {
            ...this._sceneStyleOverrides,
            [sceneKey]: {
                ...prev,
                textStyle: { ...(prev.textStyle ?? {}), ...value },
            },
        };
        this.recomputeEffectiveStyle();
        this.forceRerender();
    }

    public updateHighlightStyleForScene(sceneKey: string | null, value: Partial<CaptionsHighlightStyle>): void {
        if (!sceneKey) {
            // Apply globally and clear per-scene overrides for the changed keys only.
            this.highlightStyle = value;
            try {
                const keys = Object.keys(value ?? {});
                if (keys.length > 0 && this._sceneStyleOverrides && typeof this._sceneStyleOverrides === "object") {
                    const next: Record<string, SceneStyleOverride> = { ...this._sceneStyleOverrides };
                    for (const [k, ov] of Object.entries(next)) {
                        if (!ov || typeof ov !== "object") continue;
                        if (!ov.highlightStyle || typeof ov.highlightStyle !== "object") continue;
                        const hs: any = { ...(ov.highlightStyle as any) };
                        for (const prop of keys) delete hs[prop];
                        const updated: any = { ...ov };
                        if (Object.keys(hs).length === 0) delete updated.highlightStyle;
                        else updated.highlightStyle = hs;
                        if (!updated.textStyle && !updated.highlightStyle && !updated.textAlign) delete next[k];
                        else next[k] = updated;
                    }
                    this._sceneStyleOverrides = next;
                }
            } catch {}
            this.recomputeEffectiveStyle();
            this.forceRerender();
            return;
        }
        const prev = this._sceneStyleOverrides[sceneKey] ?? {};
        this._sceneStyleOverrides = {
            ...this._sceneStyleOverrides,
            [sceneKey]: {
                ...prev,
                highlightStyle: { ...(prev.highlightStyle ?? {}), ...value },
            },
        };
        this.recomputeEffectiveStyle();
        this.forceRerender();
    }

    public updateTextAlignForScene(sceneKey: string | null, value: CaptionsTextAlign): void {
        if (!sceneKey) {
            // Apply globally and clear per-scene overrides for this property.
            this.textAlign = value;
            try {
                if (this._sceneStyleOverrides && typeof this._sceneStyleOverrides === "object") {
                    const next: Record<string, SceneStyleOverride> = { ...this._sceneStyleOverrides };
                    for (const [k, ov] of Object.entries(next)) {
                        if (!ov || typeof ov !== "object") continue;
                        if (ov.textAlign === undefined) continue;
                        const updated: any = { ...ov };
                        delete updated.textAlign;
                        if (!updated.textStyle && !updated.highlightStyle && !updated.textAlign) delete next[k];
                        else next[k] = updated;
                    }
                    this._sceneStyleOverrides = next;
                }
            } catch {}
            this.recomputeEffectiveStyle();
            this.forceRerender();
            return;
        }
        const prev = this._sceneStyleOverrides[sceneKey] ?? {};
        this._sceneStyleOverrides = {
            ...this._sceneStyleOverrides,
            [sceneKey]: {
                ...prev,
                textAlign: value,
            },
        };
        this.recomputeEffectiveStyle();
        this.forceRerender();
    }

    public updateMaxWidthForScene(sceneKey: string | null, value: number | string): void {
        if (!sceneKey) {
            // Apply globally and clear per-scene overrides for this property.
            this.maxWidth = value;
            try {
                if (this._sceneStyleOverrides && typeof this._sceneStyleOverrides === "object") {
                    const next: Record<string, SceneStyleOverride> = { ...this._sceneStyleOverrides };
                    for (const [k, ov] of Object.entries(next)) {
                        if (!ov || typeof ov !== "object") continue;
                        if (ov.maxWidth === undefined) continue;
                        const updated: any = { ...ov };
                        delete updated.maxWidth;
                        if (!updated.textStyle && !updated.highlightStyle && !updated.textAlign && updated.maxWidth === undefined) delete next[k];
                        else next[k] = updated;
                    }
                    this._sceneStyleOverrides = next;
                }
            } catch {}
            this.recomputeEffectiveStyle();
            this.forceRerender();
            return;
        }
        const prev = this._sceneStyleOverrides[sceneKey] ?? {};
        this._sceneStyleOverrides = {
            ...this._sceneStyleOverrides,
            [sceneKey]: {
                ...prev,
                maxWidth: value,
            },
        };
        this.recomputeEffectiveStyle();
        this.forceRerender();
    }

    private forceRerender(): void {
        this._currentPhraseIndex = -1;
        this._currentWordIndex = -1;
        this._currentVisibleWordCount = 0;
        this.trigger('update', undefined);
    }

    private getScriptClip(): any | null {
        const comp: any = (this.track as any)?.composition;
        if (!comp) return null;
        const scriptId = this.scriptClipId ?? comp?.scriptLayer?.clipId;
        if (!scriptId) return null;
        const tracks: any[] = Array.isArray(comp?.tracks) ? comp.tracks : [];
        for (const t of tracks) {
            const clips: any[] = Array.isArray(t?.clips) ? t.clips : [];
            const found = clips.find((c) => c && String(c.id) === String(scriptId));
            if (found) return found;
        }
        return null;
    }

    private getSceneKeyForScene(scene: ScriptLayerScene | null | undefined): string | null {
        if (!scene) return null;
        const key = scene.startSplitId ? String(scene.startSplitId) : "__start__";
        return key;
    }

    private recomputeEffectiveStyle(sceneKey?: string | null): void {
        const key = sceneKey ?? this._currentSceneKey;
        const override = key ? this._sceneStyleOverrides[key] : undefined;
        this._effectiveTextStyle = { ...this._textStyle, ...(override?.textStyle ?? {}) };
        this._effectiveHighlightStyle = { ...this._highlightStyle, ...(override?.highlightStyle ?? {}) };
        this._effectiveTextAlign = override?.textAlign ?? this._textAlign;
        this._effectiveMaxWidth = override?.maxWidth ?? this._maxWidth;
        this.applyTextStyleToPixi();
        this.updateTextAlignment();
    }

    private getVisibleWords(): CaptionWord[] {
        const words = Array.isArray(this._words) ? this._words : [];
        if (!words.length) return [];
        const scriptClip: any = this.getScriptClip();
        const segs: ScriptLayerSegment[] = Array.isArray(scriptClip?.scriptSegments) ? (scriptClip.scriptSegments as any) : [];
        if (!segs.length) {
            // Best-effort: treat all words as visible.
            return [...words].sort((a, b) => (a.startSec ?? 0) - (b.startSec ?? 0));
        }

        // Only keep words that fall fully inside a non-gap segment.
        const playable: ScriptLayerSegment[] = segs
            .filter((s: any) => !s?.isGap)
            .sort((a, b) => Number(a.srcStartSec) - Number(b.srcStartSec));

        const sorted = [...words].sort((a, b) => (a.startSec ?? 0) - (b.startSec ?? 0));
        const out: CaptionWord[] = [];

        let segIdx = 0;
        const EPS = 1e-3;
        for (const w of sorted) {
            const w0 = Number(w.startSec ?? 0);
            const w1 = Number(w.endSec ?? w0);
            if (!Number.isFinite(w0) || !Number.isFinite(w1)) continue;

            while (segIdx < playable.length && Number(playable[segIdx].srcEndSec) <= w0 + EPS) {
                segIdx++;
            }
            const seg = playable[segIdx];
            if (!seg) continue;

            const s0 = Number(seg.srcStartSec);
            const s1 = Number(seg.srcEndSec);
            if (!Number.isFinite(s0) || !Number.isFinite(s1)) continue;
            if (w0 + EPS >= s0 && w1 - EPS <= s1) out.push(w);
        }

        return out;
    }

    private mapCompTimeToScriptTime(absCompTime: number): { scriptTimeSec: number; sceneKey: string | null } | null {
        const scriptClip: any = this.getScriptClip();
        if (!scriptClip) return null;
        const segments: ScriptLayerSegment[] = Array.isArray(scriptClip?.scriptSegments) ? scriptClip.scriptSegments : [];
        if (!segments.length) {
            const local = typeof scriptClip?.CompositionTimeToClipTime === 'function'
                ? scriptClip.CompositionTimeToClipTime(absCompTime)
                : absCompTime - Number(scriptClip?.start?.seconds ?? 0);
            const scenes = typeof scriptClip?.getScenes === 'function' ? (scriptClip.getScenes() as ScriptLayerScene[]) : [];
            const sceneIndex = typeof scriptClip?.findSceneIndexForTime === 'function' ? scriptClip.findSceneIndexForTime(local) : -1;
            const scene = scenes.find((s) => Number(s?.index) === Number(sceneIndex));
            return { scriptTimeSec: local, sceneKey: this.getSceneKeyForScene(scene) };
        }

        // Find the segment containing this absolute composition time.
        for (const seg of segments) {
            const segDur = Number(seg.srcEndSec) - Number(seg.srcStartSec);
            const segEndComp = Number(seg.compStartSec) + segDur;
            if (absCompTime >= Number(seg.compStartSec) && absCompTime < segEndComp) {
                // Gaps should show no captions (silence).
                if ((seg as any).isGap) return null;
                const offsetIntoSeg = absCompTime - Number(seg.compStartSec);
                const scriptTimeSec = Number(seg.srcStartSec) + offsetIntoSeg;
                const scenes = typeof scriptClip?.getScenes === 'function' ? (scriptClip.getScenes() as ScriptLayerScene[]) : [];
                const sceneIndex =
                    typeof scriptClip?.findSceneIndexForTime === 'function'
                        ? scriptClip.findSceneIndexForTime(scriptTimeSec)
                        : -1;
                const scene = scenes.find((s) => Number(s?.index) === Number(sceneIndex));
                return { scriptTimeSec, sceneKey: this.getSceneKeyForScene(scene) };
            }
        }

        return null;
    }

    // ============ Override update() to map time via script segments ============

    @visualize
    public override update(time: Timestamp): void | Promise<void> {
        const absCompTime = time.seconds;

        // Ensure phrases are computed (and re-computed when script segments change).
        const scriptClip: any = this.getScriptClip();
        const segs: ScriptLayerSegment[] = Array.isArray(scriptClip?.scriptSegments) ? scriptClip.scriptSegments : [];
        if (segs.length && this._lastScriptSegmentsRef !== segs) {
            this._lastScriptSegmentsRef = segs;
            this._phrasesDirty = true;
        }

        const mapped = this.mapCompTimeToScriptTime(absCompTime);
        if (!mapped) {
            this.clearDisplay();
            this._currentSceneKey = null;
            return;
        }

        if (mapped.sceneKey !== this._currentSceneKey) {
            this._currentSceneKey = mapped.sceneKey;
            this.recomputeEffectiveStyle(mapped.sceneKey);
            this.forceRerender();
        }

        if (this._phrasesDirty) {
            this.computePhrases();
        }

        if (this._displayMode === 'word') {
            this.updateWordMode(mapped.scriptTimeSec);
            return;
        }

        if (this._displayMode === 'typewriter') {
            this.updateTypewriterMode(mapped.scriptTimeSec);
            return;
        }

        this.updatePhraseMode(mapped.scriptTimeSec);
    }
}
